import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { Symbology } from '../../types/symbology';
import type { AttributeField } from '../../types/layerCreation';
import { API_BASE } from '../../config/api';
import { apiFetch, buildAuthHeaders } from '../../services/apiClient';
import { normalizeFeaturesForMap } from '../../utils/geojsonToVectorLayer';

export interface Layer {
  id: string;
  name: string;
  type: 'vector' | 'raster';
  geometryType?: 'Point' | 'LineString' | 'Polygon' | 'MultiPolygon';
  visible: boolean;
  opacity: number;
  zIndex: number;
  featureCount?: number;
  features?: Feature[]; // Add features array for rendering
  symbology?: Symbology;
  crs?: string;
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
  source?: string;
  style?: Record<string, unknown>;
  attributeFields?: AttributeField[];
  // Raster specific for COG/XYZ etc (for non-tech and pro features robustness)
  sourceType?: 'cog' | 'xyz' | 'wms' | 'wmts';
  sourceUrl?: string;
  renderStyle?: Record<string, any>;
}

// Feature interface to match the backend structure
interface Feature {
  id: string;
  layerId: string;
  geometry: GeoJSON.Geometry;
  properties: Record<string, any>;
}

interface LayersState {
  layers: Layer[];
  activeLayerId: string | null;
  isLoading: boolean;
  error: string | null;
  geometryUpdateRequestIds: Record<string, string>;
}

const initialState: LayersState = {
  layers: [],
  activeLayerId: null,
  isLoading: false,
  error: null,
  geometryUpdateRequestIds: {},
};

// Canonical API base (config/api.ts → VITE_API_URL or localhost:3001)
const API_BASE_URL = API_BASE;

/**
 * Get headers with authentication and CSRF tokens
 */
const getAuthHeaders = (): HeadersInit => buildAuthHeaders();

/**
 * Resilient layer API call — timeout + retry for GET.
 * `path` is relative to /api (e.g. "/layers" or "/layers/id/features").
 */
const layersFetch = async (
  path: string,
  init: RequestInit & { retry?: boolean } = {}
): Promise<Response> => {
  const rel = path.startsWith('/') ? path : `/${path}`;
  const method = (init.method || 'GET').toUpperCase();
  let body: unknown;
  if (init.body && typeof init.body === 'string') {
    try {
      body = JSON.parse(init.body);
    } catch {
      body = undefined;
    }
  }
  return apiFetch(`/api${rel.replace(/^\/api/, '')}`, {
    method,
    body: method === 'GET' || method === 'HEAD' ? undefined : body,
    headers: init.headers,
    retry: init.retry ?? (method === 'GET' || method === 'HEAD'),
  });
};

// Async thunks for API integration

/**
 * Fetch all layers for the current user with their features
 */
const fetchLayers = createAsyncThunk('layers/fetchLayers', async (_, { rejectWithValue }) => {
  try {
    const response = await layersFetch(`/layers`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      return rejectWithValue(errorData.message || 'Failed to fetch layers');
    }

    const data = await response.json();
    const layers = data.data; // API returns { success: true, data: layers[] }

    // Fetch features for each layer
    const layersWithFeatures = await Promise.all(
      layers.map(async (layer: any) => {
        try {
          const featuresResponse = await layersFetch(`/layers/${layer.id}/features`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
          });

          if (featuresResponse.ok) {
            const featuresData = await featuresResponse.json();
            return {
              ...layer,
              features: featuresData.data || [],
            };
          } else {
            // If features fetch fails, return layer without features
            console.warn(`Failed to fetch features for layer ${layer.id}`);
            return {
              ...layer,
              features: [],
            };
          }
        } catch (error) {
          console.warn(`Error fetching features for layer ${layer.id}:`, error);
          return {
            ...layer,
            features: [],
          };
        }
      })
    );

    return layersWithFeatures;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Network error');
  }
});

/**
 * Create a new layer
 */
const createLayerAsync = createAsyncThunk(
  'layers/createLayer',
  async (
    layerData: {
      name: string;
      geometryType: 'Point' | 'LineString' | 'Polygon';
      crs?: string;
      symbology?: Record<string, unknown>;
      visible?: boolean;
      zOrder?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await layersFetch(`/layers`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(layerData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to create layer');
      }

      const data = await response.json();
      return data.data; // API returns { success: true, data: layer }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Network error');
    }
  }
);

/**
 * Update a layer
 */
const updateLayerAsync = createAsyncThunk(
  'layers/updateLayer',
  async ({ id, updates }: { id: string; updates: Partial<Layer> }, { rejectWithValue }) => {
    // Client-side layers (demo, local, temp, file) do not exist in backend DB
    if (
      id.startsWith('demo-') ||
      id.startsWith('local-') ||
      id.startsWith('temp-') ||
      id.startsWith('file-')
    ) {
      return { id, updates };
    }

    try {
      const response = await layersFetch(`/layers/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        if (response.status === 404) {
          // If layer missing on server, perform client-side update
          return { id, updates };
        }
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData.message || 'Failed to update layer');
      }

      const data = await response.json();
      return { id, updates, data: data.data }; // API returns { success: true, data: layer }
    } catch (error) {
      // Fallback to local state update if network error
      return { id, updates };
    }
  }
);

/**
 * Delete a layer
 */
const deleteLayerAsync = createAsyncThunk(
  'layers/deleteLayer',
  async (layerId: string, { rejectWithValue }) => {
    // Client-side demo/local layers do not exist in backend DB
    if (
      layerId.startsWith('demo-') ||
      layerId.startsWith('local-') ||
      layerId.startsWith('temp-') ||
      layerId.startsWith('file-')
    ) {
      return layerId;
    }

    try {
      const response = await layersFetch(`/layers/${layerId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          // Layer already missing on server — still remove locally
          return layerId;
        }
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData.message || 'Failed to delete layer');
      }

      return layerId; // Return the deleted layer ID
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete layer');
    }
  }
);

/**
 * Update layer order
 */
const updateLayerOrderAsync = createAsyncThunk(
  'layers/updateLayerOrder',
  async (layerIds: string[], { rejectWithValue }) => {
    try {
      const response = await layersFetch(`/layers/order`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ layerIds }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to update layer order');
      }

      return layerIds; // Return the new order
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Network error');
    }
  }
);

/**
 * Update layer symbology - persists to the dedicated symbology endpoint
 */
const updateSymbologyAsync = createAsyncThunk(
  'layers/updateSymbology',
  async (
    { layerId, symbology }: { layerId: string; symbology: Symbology },
    { rejectWithValue }
  ) => {
    try {
      const response = await layersFetch(`/layers/${layerId}/symbology`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ symbology }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to update symbology');
      }

      return { layerId, symbology };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Network error');
    }
  }
);

/**
 * Add a feature to a layer - posts geometry to the backend and updates Redux state
 */
const addFeatureAsync = createAsyncThunk(
  'layers/addFeature',
  async (
    {
      layerId,
      geometry,
      properties,
    }: { layerId: string; geometry: GeoJSON.Geometry; properties?: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      const response = await layersFetch(`/layers/${layerId}/features`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ geometry, properties }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to add feature');
      }

      const data = await response.json();
      return { layerId, feature: data.data as Feature };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Network error');
    }
  }
);

interface UpdateFeatureGeometryArgs {
  layerId: string;
  featureId: string;
  geometry: GeoJSON.Geometry;
  previousGeometry: GeoJSON.Geometry;
}

const updateFeatureGeometryAsync = createAsyncThunk(
  'layers/updateFeatureGeometry',
  async ({ layerId, featureId, geometry }: UpdateFeatureGeometryArgs, { rejectWithValue }) => {
    if (
      layerId.startsWith('demo-') ||
      layerId.startsWith('local-') ||
      layerId.startsWith('temp-') ||
      layerId.startsWith('file-')
    ) {
      return { layerId, featureId, geometry };
    }

    try {
      const response = await layersFetch(`/layers/features/${featureId}/geometry`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ geometry }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData.message || 'Failed to update feature geometry');
      }

      const data = await response.json();
      return {
        layerId,
        featureId,
        geometry: data.data.geometry as GeoJSON.Geometry,
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update feature geometry'
      );
    }
  }
);

/**
 * Helper to generate a client-side layer duplicate
 */
function createClientDuplicate(
  sourceLayer: Layer,
  customName?: string,
  existingLayers: Layer[] = []
): Layer {
  let duplicateName = customName;
  if (!duplicateName) {
    const copyPattern = new RegExp(
      `^${sourceLayer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\(Copy(?:\\s*(\\d+))?\\)$`,
      'i'
    );
    let maxCopy = 0;
    existingLayers.forEach((l) => {
      const match = l.name.match(copyPattern);
      if (match) {
        const num = match[1] ? parseInt(match[1], 10) : 1;
        if (num > maxCopy) maxCopy = num;
      }
    });
    duplicateName =
      maxCopy === 0 ? `${sourceLayer.name} (Copy)` : `${sourceLayer.name} (Copy ${maxCopy + 1})`;
  }

  const newId = `local-copy-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
  const maxZ = existingLayers.reduce((max, l) => Math.max(max, l.zIndex ?? 0), 0);

  const newFeatures = (sourceLayer.features || []).map((f, i) => ({
    ...f,
    id: `feat-${Date.now()}-${i}-${Math.random().toString(36).substring(2, 4)}`,
    layerId: newId,
    properties: { ...(f.properties || {}) },
  }));

  return {
    ...sourceLayer,
    id: newId,
    name: duplicateName,
    zIndex: maxZ + 1,
    featureCount: newFeatures.length,
    features: newFeatures,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Duplicate a layer with all its features and symbology
 */
const duplicateLayerAsync = createAsyncThunk(
  'layers/duplicateLayer',
  async ({ layerId, name }: { layerId: string; name?: string }, { rejectWithValue, getState }) => {
    const state = getState() as { layers: LayersState };
    const sourceLayer = state.layers.layers.find((l) => l.id === layerId);

    const isClientLayer =
      layerId.startsWith('demo-') ||
      layerId.startsWith('local-') ||
      layerId.startsWith('temp-') ||
      layerId.startsWith('file-');

    if (isClientLayer && sourceLayer) {
      return createClientDuplicate(sourceLayer, name, state.layers.layers);
    }

    try {
      const response = await layersFetch(`/layers/${layerId}/duplicate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ name }),
      });

      if (!response.ok) {
        if (response.status === 404 && sourceLayer) {
          return createClientDuplicate(sourceLayer, name, state.layers.layers);
        }
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData.message || 'Failed to duplicate layer');
      }

      const data = await response.json();
      return data.data; // API returns { success: true, data: layer }
    } catch (error) {
      if (sourceLayer) {
        return createClientDuplicate(sourceLayer, name, state.layers.layers);
      }
      return rejectWithValue(error instanceof Error ? error.message : 'Network error');
    }
  }
);

// =====================================================
// RASTER LAYER SUPPORT (QGIS-like raster layers: COG/XYZ/WMS)
// =====================================================

const fetchRasterLayers = createAsyncThunk(
  'layers/fetchRasterLayers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await layersFetch(`/raster`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json();
        return rejectWithValue(err.message || 'Failed to fetch raster layers');
      }
      const data = await response.json();
      return data.data || [];
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Network error');
    }
  }
);

const createRasterLayerAsync = createAsyncThunk(
  'layers/createRasterLayer',
  async (
    rasterData: {
      name: string;
      sourceType: 'cog' | 'xyz' | 'wms' | 'wmts';
      sourceUrl: string;
      sourceConfig?: Record<string, any>;
      renderStyle?: Record<string, any>;
      crs?: string;
      visible?: boolean;
      opacity?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await layersFetch(`/raster`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(rasterData),
      });
      if (!response.ok) {
        const err = await response.json();
        return rejectWithValue(err.message || 'Failed to create raster layer');
      }
      const data = await response.json();
      return data.data;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Network error');
    }
  }
);

const updateRasterLayerAsync = createAsyncThunk(
  'layers/updateRasterLayer',
  async ({ id, updates }: { id: string; updates: Partial<Layer> }, { rejectWithValue }) => {
    try {
      const response = await layersFetch(`/raster/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (!response.ok) {
        const err = await response.json();
        return rejectWithValue(err.message || 'Failed to update raster layer');
      }
      const data = await response.json();
      return data.data;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Network error');
    }
  }
);

const deleteRasterLayerAsync = createAsyncThunk(
  'layers/deleteRasterLayer',
  async (rasterId: string, { rejectWithValue }) => {
    try {
      const response = await layersFetch(`/raster/${rasterId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json();
        return rejectWithValue(err.message || 'Failed to delete raster layer');
      }
      return rasterId;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Network error');
    }
  }
);

const layersSlice = createSlice({
  name: 'layers',
  initialState,
  reducers: {
    addLayer: (state, action: PayloadAction<Layer>) => {
      if (state.layers.some((layer) => layer.id === action.payload.id)) return;
      state.layers.push(action.payload);
    },
    /** Replace entire workspace layer list (project open / restore) */
    replaceAllLayers: (
      state,
      action: PayloadAction<{ layers: Layer[]; activeLayerId?: string | null }>
    ) => {
      state.layers = action.payload.layers;
      state.activeLayerId =
        action.payload.activeLayerId !== undefined
          ? action.payload.activeLayerId
          : (action.payload.layers[0]?.id ?? null);
      state.error = null;
    },
    removeLayer: (state, action: PayloadAction<string>) => {
      state.layers = state.layers.filter((layer) => layer.id !== action.payload);
      if (state.activeLayerId === action.payload) {
        state.activeLayerId = null;
      }
    },
    updateLayer: (state, action: PayloadAction<{ id: string; updates: Partial<Layer> }>) => {
      const layer = state.layers.find((l) => l.id === action.payload.id);
      if (layer) {
        Object.assign(layer, action.payload.updates);
      }
    },
    toggleLayerVisibility: (state, action: PayloadAction<string>) => {
      const layer = state.layers.find((l) => l.id === action.payload);
      if (layer) {
        layer.visible = !layer.visible;
      }
    },
    setLayerOpacity: (state, action: PayloadAction<{ id: string; opacity: number }>) => {
      const layer = state.layers.find((l) => l.id === action.payload.id);
      if (layer) {
        layer.opacity = action.payload.opacity;
      }
    },
    reorderLayers: (state, action: PayloadAction<string[]>) => {
      const layerMap = new Map(state.layers.map((l) => [l.id, l]));
      state.layers = action.payload
        .map((id) => layerMap.get(id))
        .filter((l): l is Layer => l !== undefined);
    },
    setActiveLayer: (state, action: PayloadAction<string | null>) => {
      state.activeLayerId = action.payload;
    },
    setLayersLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setLayersError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearLayersError: (state) => {
      state.error = null;
    },
    updateLayerSymbology: (state, action: PayloadAction<{ id: string; symbology: Symbology }>) => {
      const layer = state.layers.find((l) => l.id === action.payload.id);
      if (layer) {
        layer.symbology = action.payload.symbology;
      }
    },
    updateFeatureAttribute: (
      state,
      action: PayloadAction<{
        layerId: string;
        featureId: string;
        field: string;
        value: any;
      }>
    ) => {
      const { layerId, featureId, field, value } = action.payload;
      const layer = state.layers.find((l) => l.id === layerId);
      if (layer?.features) {
        const feature = layer.features.find((f) => f.id === featureId);
        if (feature) {
          if (!feature.properties) {
            feature.properties = {};
          }
          feature.properties[field] = value;
        }
      }
    },
    /**
     * Update the geometry of a specific feature within a layer.
     * Used by node editing operations (add/remove/move node).
     */
    updateFeatureGeometry: (
      state,
      action: PayloadAction<{
        layerId: string;
        featureId: string;
        geometry: GeoJSON.Geometry;
      }>
    ) => {
      const { layerId, featureId, geometry } = action.payload;
      const layer = state.layers.find((l) => l.id === layerId);
      if (layer?.features) {
        const feature = layer.features.find((f) => f.id === featureId);
        if (feature) {
          feature.geometry = geometry;
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch layers
    builder
      .addCase(fetchLayers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLayers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.layers = action.payload.map((layer: any) => {
          const features = normalizeFeaturesForMap(
            (layer.features || []).map((f: any, i: number) => ({
              id: f.id ?? `f-${i}`,
              layerId: layer.id,
              geometry: f.geometry,
              properties: f.properties || {},
            }))
          );
          return {
            id: layer.id,
            name: layer.name,
            type: 'vector' as const,
            geometryType: layer.geometryType,
            visible: layer.visible,
            // Use opacity from symbology if available, otherwise default to 1
            opacity: layer.symbology?.opacity ?? 1,
            zIndex: layer.zOrder,
            featureCount: features.length || layer.featureCount,
            features, // GeometryCollection exploded; unrenderable dropped
            symbology: layer.symbology,
            crs: layer.crs,
            userId: layer.userId,
            createdAt: layer.createdAt,
            updatedAt: layer.updatedAt,
          };
        });
      })
      .addCase(fetchLayers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create layer
    builder
      .addCase(createLayerAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createLayerAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const newLayer: Layer = {
          id: action.payload.id,
          name: action.payload.name,
          type: 'vector',
          geometryType: action.payload.geometryType,
          visible: action.payload.visible,
          opacity: 1,
          zIndex: action.payload.zOrder,
          featureCount: 0,
          features: [], // New layer starts with no features
          symbology: action.payload.symbology,
          crs: action.payload.crs,
          userId: action.payload.userId,
          createdAt: action.payload.createdAt,
          updatedAt: action.payload.updatedAt,
        };
        state.layers.push(newLayer);
      })
      .addCase(createLayerAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update layer
    builder
      .addCase(updateLayerAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(updateLayerAsync.fulfilled, (state, action) => {
        const payload = action.payload as any;
        const id = payload.id;
        const updates = payload.updates || {};
        const data = payload.data || {};

        const index = state.layers.findIndex((l) => l.id === id);
        if (index !== -1) {
          const current = state.layers[index];
          state.layers[index] = {
            ...current,
            name: data.name ?? updates.name ?? current.name,
            geometryType: data.geometryType ?? updates.geometryType ?? current.geometryType,
            visible: data.visible ?? updates.visible ?? current.visible,
            zIndex: data.zOrder ?? data.zIndex ?? updates.zIndex ?? current.zIndex,
            symbology: data.symbology ?? updates.symbology ?? current.symbology,
            crs: data.crs ?? updates.crs ?? current.crs,
            updatedAt: data.updatedAt ?? new Date().toISOString(),
          };
        }
      })
      .addCase(updateLayerAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete layer
    builder
      .addCase(deleteLayerAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteLayerAsync.fulfilled, (state, action) => {
        state.layers = state.layers.filter((layer) => layer.id !== action.payload);
        if (state.activeLayerId === action.payload) {
          state.activeLayerId = state.layers[0]?.id ?? null;
        }
      })
      .addCase(deleteLayerAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Update layer order
    builder
      .addCase(updateLayerOrderAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(updateLayerOrderAsync.fulfilled, (state, action) => {
        // Update the z-index based on the new order
        const layerMap = new Map(state.layers.map((l) => [l.id, l]));
        action.payload.forEach((layerId, index) => {
          const layer = layerMap.get(layerId);
          if (layer) {
            layer.zIndex = index;
          }
        });
        // Sort layers by new z-index
        state.layers.sort((a, b) => a.zIndex - b.zIndex);
      })
      .addCase(updateLayerOrderAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Update symbology
    builder
      .addCase(updateSymbologyAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(updateSymbologyAsync.fulfilled, (state, action) => {
        const layer = state.layers.find((l) => l.id === action.payload.layerId);
        if (layer) {
          layer.symbology = action.payload.symbology;
        }
      })
      .addCase(updateSymbologyAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Add feature
    builder
      .addCase(addFeatureAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(addFeatureAsync.fulfilled, (state, action) => {
        const layer = state.layers.find((l) => l.id === action.payload.layerId);
        if (layer) {
          if (!layer.features) layer.features = [];
          layer.features.push(action.payload.feature);
          layer.featureCount = (layer.featureCount ?? 0) + 1;
        }
      })
      .addCase(addFeatureAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Persist node geometry edits optimistically and roll back only the latest request.
    builder
      .addCase(updateFeatureGeometryAsync.pending, (state, action) => {
        const { layerId, featureId, geometry } = action.meta.arg;
        const key = `${layerId}::${featureId}`;
        state.geometryUpdateRequestIds[key] = action.meta.requestId;
        state.error = null;

        const feature = state.layers
          .find((layer) => layer.id === layerId)
          ?.features?.find((item) => item.id === featureId);
        if (feature) feature.geometry = geometry;
      })
      .addCase(updateFeatureGeometryAsync.fulfilled, (state, action) => {
        const { layerId, featureId, geometry } = action.payload;
        const key = `${layerId}::${featureId}`;
        if (state.geometryUpdateRequestIds[key] !== action.meta.requestId) return;

        const feature = state.layers
          .find((layer) => layer.id === layerId)
          ?.features?.find((item) => item.id === featureId);
        if (feature) feature.geometry = geometry;
        delete state.geometryUpdateRequestIds[key];
      })
      .addCase(updateFeatureGeometryAsync.rejected, (state, action) => {
        const { layerId, featureId, previousGeometry } = action.meta.arg;
        const key = `${layerId}::${featureId}`;
        if (state.geometryUpdateRequestIds[key] !== action.meta.requestId) return;

        const feature = state.layers
          .find((layer) => layer.id === layerId)
          ?.features?.find((item) => item.id === featureId);
        if (feature) feature.geometry = previousGeometry;
        delete state.geometryUpdateRequestIds[key];
        state.error = action.payload as string;
      });

    // Duplicate layer
    builder
      .addCase(duplicateLayerAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(duplicateLayerAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload;
        const rawFeatures = payload.features || [];
        const features = normalizeFeaturesForMap(
          rawFeatures.map((f: any, i: number) => ({
            id: f.id ?? `f-${i}`,
            layerId: payload.id,
            geometry: f.geometry,
            properties: f.properties || {},
          }))
        );

        const newLayer: Layer = {
          id: payload.id,
          name: payload.name,
          type: 'vector',
          geometryType: payload.geometryType,
          visible: payload.visible ?? true,
          opacity: payload.opacity ?? 1,
          zIndex: payload.zOrder ?? payload.zIndex ?? state.layers.length + 1,
          featureCount: features.length,
          features,
          symbology: payload.symbology,
          crs: payload.crs,
          userId: payload.userId,
          createdAt: payload.createdAt ?? new Date().toISOString(),
          updatedAt: payload.updatedAt ?? new Date().toISOString(),
        };
        state.layers.push(newLayer);
        state.activeLayerId = newLayer.id;
      })
      .addCase(duplicateLayerAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // --- RASTER LAYERS ---
    builder
      .addCase(fetchRasterLayers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchRasterLayers.fulfilled, (state, action) => {
        state.isLoading = false;
        const existingNonRaster = state.layers.filter((l) => l.type !== 'raster');
        const rasters = (action.payload || []).map((r: any) => ({
          id: r.id,
          name: r.name,
          type: 'raster' as const,
          visible: r.visible,
          opacity: r.opacity ?? 0.85,
          zIndex: r.zOrder ?? 5,
          sourceType: r.sourceType,
          sourceUrl: r.sourceUrl,
          renderStyle: r.renderStyle,
          crs: r.crs,
          userId: r.userId,
        }));
        state.layers = [...existingNonRaster, ...rasters];
      })
      .addCase(fetchRasterLayers.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    builder.addCase(createRasterLayerAsync.fulfilled, (state, action) => {
      const r = action.payload;
      state.layers.push({
        id: r.id,
        name: r.name,
        type: 'raster' as const,
        visible: r.visible,
        opacity: r.opacity ?? 0.85,
        zIndex: r.zOrder ?? 5,
        sourceType: r.sourceType,
        sourceUrl: r.sourceUrl,
        renderStyle: r.renderStyle,
        crs: r.crs,
      });
    });

    builder.addCase(deleteRasterLayerAsync.fulfilled, (state, action) => {
      state.layers = state.layers.filter((l) => l.id !== action.payload);
    });
  },
});

export const {
  addLayer,
  replaceAllLayers,
  removeLayer,
  updateLayer,
  toggleLayerVisibility,
  setLayerOpacity,
  reorderLayers,
  setActiveLayer,
  setLayersLoading,
  setLayersError,
  clearLayersError,
  updateLayerSymbology,
  updateFeatureAttribute,
  updateFeatureGeometry,
} = layersSlice.actions;

export { layersSlice };

export {
  fetchLayers,
  createLayerAsync,
  updateLayerAsync,
  deleteLayerAsync,
  updateLayerOrderAsync,
  duplicateLayerAsync,
  updateSymbologyAsync,
  addFeatureAsync,
  updateFeatureGeometryAsync,
  fetchRasterLayers,
  createRasterLayerAsync,
  updateRasterLayerAsync,
  deleteRasterLayerAsync,
};

export default layersSlice.reducer;
